// Shared constants and utilities for document templates
import { 
  Shield, HeartPulse, User, Briefcase, DollarSign, FolderOpen, List, Layers 
} from 'lucide-react';

export const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category colors for templates
export const categoryColors = {
  pleadings: 'bg-red-100 text-red-700',
  discovery: 'bg-blue-100 text-blue-700',
  correspondence: 'bg-green-100 text-green-700',
  contracts: 'bg-purple-100 text-purple-700',
  templates: 'bg-slate-100 text-slate-700',
  internal: 'bg-amber-100 text-amber-700',
  custom: 'bg-indigo-100 text-indigo-700',
  other: 'bg-gray-100 text-gray-700'
};

// Group colors
export const groupColors = {
  blue: 'bg-blue-100 text-blue-700 border-blue-200',
  red: 'bg-red-100 text-red-700 border-red-200',
  green: 'bg-green-100 text-green-700 border-green-200',
  purple: 'bg-purple-100 text-purple-700 border-purple-200',
  amber: 'bg-amber-100 text-amber-700 border-amber-200',
  slate: 'bg-slate-100 text-slate-700 border-slate-200',
  indigo: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  pink: 'bg-pink-100 text-pink-700 border-pink-200',
  cyan: 'bg-cyan-100 text-cyan-700 border-cyan-200'
};

// Group icons mapping
export const groupIcons = {
  'shield': Shield,
  'heart-pulse': HeartPulse,
  'user': User,
  'briefcase': Briefcase,
  'dollar-sign': DollarSign,
  'folder': FolderOpen,
  'list': List,
  'layers': Layers
};

// Field type labels
export const fieldTypeLabels = {
  text: 'Text',
  textarea: 'Multi-line Text',
  number: 'Number',
  currency: 'Currency',
  date: 'Date',
  email: 'Email',
  phone: 'Phone',
  select: 'Dropdown',
  checkbox: 'Checkbox',
  calculation: 'Calculation'
};

// Calculation operators
export const calculationOperators = {
  sum: 'Sum (Add all)',
  subtract: 'Subtract',
  multiply: 'Multiply',
  divide: 'Divide',
  average: 'Average',
  min: 'Minimum',
  max: 'Maximum',
  percentage: 'Percentage',
  custom: 'Custom Formula'
};

// Available icons for groups
export const availableIcons = [
  { value: 'shield', label: 'Shield' },
  { value: 'heart-pulse', label: 'Medical' },
  { value: 'user', label: 'User' },
  { value: 'briefcase', label: 'Briefcase' },
  { value: 'dollar-sign', label: 'Dollar' },
  { value: 'folder', label: 'Folder' },
  { value: 'list', label: 'List' },
  { value: 'layers', label: 'Layers' }
];

// Available colors for groups
export const availableColors = [
  { value: 'blue', label: 'Blue' },
  { value: 'red', label: 'Red' },
  { value: 'green', label: 'Green' },
  { value: 'purple', label: 'Purple' },
  { value: 'amber', label: 'Amber' },
  { value: 'slate', label: 'Slate' },
  { value: 'indigo', label: 'Indigo' },
  { value: 'pink', label: 'Pink' },
  { value: 'cyan', label: 'Cyan' }
];

// Utility functions
export const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

export const generateTag = (name) => {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
};

export const getGroupIcon = (iconName) => {
  return groupIcons[iconName] || FolderOpen;
};

// Initial custom field data
export const initialCustomFieldData = {
  name: '',
  tag: '',
  description: '',
  field_type: 'text',
  placeholder: '',
  default_value: '',
  options: '',
  required: false,
  category: 'custom',
  group_id: '',
  validation: {
    min_length: '',
    max_length: '',
    pattern: '',
    pattern_description: '',
    min_value: '',
    max_value: '',
    required_for_case_types: '',
    custom_error_message: ''
  },
  dependency: {
    depends_on: '',
    condition: 'exists',
    value: '',
    make_required: false
  },
  calculation: {
    operator: 'sum',
    source_fields: [],
    custom_formula: '',
    round_to: 2,
    format_as_currency: true,
    state_overrides: [],
    case_type_formulas: {},
    description: ''
  }
};

// Initial group data
export const initialGroupData = {
  name: '',
  description: '',
  icon: 'folder',
  color: 'slate'
};
